import { analytics, globalObservability } from '@opendoor/observability/slim';

import type { SellerLeadChannel } from '../../__generated__/zeus/types';
import { doGet } from './';
import type { IAddressInputAddress } from './addressTypes';
import { redactGraphqlRequestErrorForTelemetry } from './graphqlRequestError';
import {
  parseSellerLeadChannel,
  SellerAddressIntakeError,
  startSellerApplication,
} from './sellerAddressIntake';

const Sentry = globalObservability.getSentryClient();

const sellerApplicationChannelOverride = (): SellerLeadChannel | undefined => {
  const params = new URLSearchParams(window.location.search);
  return parseSellerLeadChannel(params.get('channel_override') ?? undefined);
};

const sellerTrackingPath = (trackingKeywords: string | undefined): string | undefined => {
  const keywordPath = trackingKeywords?.trim();
  if (!keywordPath) return undefined;

  const segments = keywordPath.split('/');
  // URL parsing normalizes dot-only path segments before the credentialed request is sent.
  if (segments.some((segment) => segment === '.' || segment === '..')) return undefined;

  try {
    return `/track/${segments.map((segment) => encodeURIComponent(segment)).join('/')}`;
  } catch {
    return undefined;
  }
};

type AddressVerificationError = Pick<SellerAddressIntakeError, 'kind' | 'message'>;

type StartAddressVerificationResult =
  | {
      status: 'success';
      applicationId: string;
      pdpUrl?: string;
    }
  | {
      status: 'error';
      error: AddressVerificationError;
    };

export const startAddressVerification = async ({
  addressUUID,
  address,
  channel,
  trackingKeywords,
  inputLocation,
  partnerReferralId,
  userConfirmed,
}: {
  addressUUID?: string;
  address: IAddressInputAddress;
  channel?: SellerLeadChannel;
  trackingKeywords?: string;
  inputLocation?: string;
  partnerReferralId?: string;
  userConfirmed?: boolean;
}): Promise<StartAddressVerificationResult> => {
  // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
  analytics.trackEvent('inputting', 'address', 'verifying', {
    ...address,
    inputLocation,
  });

  const trackingPath = sellerTrackingPath(trackingKeywords);
  const resolvedChannel = sellerApplicationChannelOverride() ?? channel ?? 'DEFAULT';

  try {
    if (trackingPath) {
      await doGet(trackingPath).catch((trackingError) =>
        Sentry.captureException?.(redactGraphqlRequestErrorForTelemetry(trackingError)),
      );
    }
    const { applicationId, pdpUrl, postalCode } = await startSellerApplication({
      address,
      addressId: addressUUID,
      channel: resolvedChannel,
      partnerReferralId,
      userConfirmed,
    });
    const submittedPostalCode = address.postal_code.trim();
    if (submittedPostalCode && postalCode !== submittedPostalCode) {
      throw new SellerAddressIntakeError(
        'postal-code-mismatch',
        'Seller application postal code did not match the submitted address',
      );
    }
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    analytics.register({ 'Offer ID': applicationId, 'Offer Postal Code': postalCode });
    return { status: 'success', applicationId, ...(pdpUrl ? { pdpUrl } : {}) };
  } catch (cause) {
    const failure: AddressVerificationError =
      cause instanceof SellerAddressIntakeError
        ? { kind: cause.kind, message: cause.message }
        : {
            kind: 'generic-error',
            message: 'Seller application creation failed',
          };
    // oxlint-disable-next-line typescript/no-deprecated -- OFFER-4581: existing legacy analytics usage is tracked for removal
    analytics.trackEvent('lead', 'failed');
    Sentry.captureException?.(redactGraphqlRequestErrorForTelemetry(cause));
    return { status: 'error', error: failure };
  }
};
